import React, { useEffect, useRef } from "react";
import { Avatar, Badge, Box, HStack, Text } from "@chakra-ui/react";
import PriorityIcon from "./PriorityIcon";
import { KIND_BADGE, SOURCE_BADGE, formatDate } from "./TaskRow";
import { isOverdue } from "./tasksModel";

// One card on the Tasks board: click (or Enter) opens it, drag moves it.
// summary: its { comments, rejection } from useWorkItemSummaries.
export default function TaskBoardCard({ item, currentUserId, summary, highlighted, busy, onOpen, onDragStart, onDragEnd }) {
  const ref = useRef(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlighted]);
  const badge = KIND_BADGE[item.kind === "order" ? item.orderType : "task"] || KIND_BADGE.order;
  const source = SOURCE_BADGE[item.sourceKind];
  const closed = item.done || item.cancelled;
  const overdue = !closed && isOverdue(item.due);
  // The board mixes both sides, so a card says which side it's on: who handed
  // it to me, or who I handed it to (nobody else on a task I gave myself).
  // Someone else's card (a channel's) names who has it.
  const toMe = item.assignedTo === currentUserId;
  const person = toMe
    ? item.assignedBy && item.assignedBy !== currentUserId && item.assignerName
      ? { label: "From", name: item.assignerName }
      : null
    : { label: "To", name: item.assigneeName || "someone" };
  const needsMyReview = item.status === "pending_review" && !toMe && item.assignedBy === currentUserId;
  const mine = toMe || item.assignedBy === currentUserId;
  const comments = summary?.comments || 0;
  return (
    <Box
      ref={ref}
      role="button"
      tabIndex={0}
      draggable={!busy && mine}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      onDragStart={(e) => onDragStart(e, item)}
      onDragEnd={onDragEnd}
      data-testid={`task-card-${item.key}`}
      data-highlighted={highlighted ? "true" : undefined}
      bg="white"
      _dark={{ bg: "gray.700" }}
      borderWidth="1px"
      borderColor={highlighted ? "blue.400" : "gray.200"}
      boxShadow={highlighted ? "0 0 0 3px rgba(66, 153, 225, 0.6)" : "sm"}
      borderRadius="md"
      p={2.5}
      cursor={busy ? "progress" : mine ? "grab" : "pointer"}
      opacity={busy ? 0.6 : closed ? 0.75 : 1}
      _hover={{ boxShadow: "md" }}
    >
      <HStack align="flex-start" spacing={2}>
        <PriorityIcon priority={item.priority} />
        <Text fontSize="sm" fontWeight="medium" noOfLines={2} textDecoration={closed ? "line-through" : "none"}>
          {item.title}
        </Text>
      </HStack>
      <HStack spacing={1} mt={1.5} flexWrap="wrap">
        <Badge colorScheme={badge.colorScheme} fontSize="2xs">{badge.label}</Badge>
        {source && <Badge colorScheme={source.colorScheme} variant="subtle" fontSize="2xs">{source.label}</Badge>}
        {needsMyReview && <Badge colorScheme="orange" fontSize="2xs">Needs your review</Badge>}
        {item.rejected && <Badge colorScheme="red" fontSize="2xs">Rejected</Badge>}
        {item.cancelled && <Badge colorScheme="gray" fontSize="2xs">Cancelled</Badge>}
        {item.patientName && <Text fontSize="xs" color="gray.600" noOfLines={1}>{item.patientName}</Text>}
      </HStack>
      {item.rejected && summary?.rejection?.body && (
        <Text fontSize="xs" color="red.600" mt={1} noOfLines={2}>“{summary.rejection.body}”</Text>
      )}
      <HStack mt={2} spacing={2} fontSize="xs" color="gray.500">
        {item.due && (
          <Text color={overdue ? "red.500" : "gray.500"} fontWeight={overdue ? "semibold" : "normal"}>
            {overdue ? "Overdue · " : ""}{formatDate(item.due)}
          </Text>
        )}
        {comments > 0 && <Text aria-label={`${comments} comments`}>💬 {comments}</Text>}
        <Box flex="1" />
        {person && (
          <HStack spacing={1} minW={0}>
            <Avatar size="2xs" name={person.name} />
            <Text noOfLines={1} maxW="110px" color="gray.600">{person.label} {person.name}</Text>
          </HStack>
        )}
      </HStack>
    </Box>
  );
}
